import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

function parseUpstreamRoute(route) {
  if (!route || typeof route !== 'string') {
    return { method: 'GET', path: '' };
  }

  const parts = route.trim().split(/\s+/);
  if (parts.length < 2) {
    return { method: 'GET', path: route };
  }

  return {
    method: parts[0].toUpperCase(),
    path: parts.slice(1).join(' '),
  };
}

function parseJsonInput(value, fallback) {
  const trimmed = String(value || '').trim();
  if (!trimmed) {
    return fallback;
  }

  return JSON.parse(trimmed);
}

function formatDateTime(value) {
  if (!value) {
    return 'N/A';
  }

  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return date.toLocaleString();
  } catch {
    return String(value);
  }
}

function statusLabelClass(status) {
  if (status >= 500) {
    return 'label label-danger';
  }
  if (status >= 400) {
    return 'label label-warning';
  }
  if (status >= 200) {
    return 'label label-success';
  }

  return 'label label-default';
}

export default function McpAdminPanel({
  userId,
  isProductionEnv = false,
  businessTemplateId,
  businessTemplateTitle,
  allowedScopes = [],
  toolDefinitions = [],
}) {
  const [selectedScopes, setSelectedScopes] = useState(() => allowedScopes.slice(0, 2));
  const [ttlMinutes, setTtlMinutes] = useState('60');
  const [label, setLabel] = useState('api-admin');
  const [issuingToken, setIssuingToken] = useState(false);
  const [brokerError, setBrokerError] = useState('');
  const [issuedToken, setIssuedToken] = useState(null);

  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [dashboardError, setDashboardError] = useState('');
  const [status, setStatus] = useState(null);
  const [tools, setTools] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [requests, setRequests] = useState([]);
  const [selectedRequestId, setSelectedRequestId] = useState('');
  const [selectedRequestDetail, setSelectedRequestDetail] = useState(null);
  const [selectedTokenId, setSelectedTokenId] = useState('');
  const [selectedTokenDetail, setSelectedTokenDetail] = useState(null);
  const [revokingToken, setRevokingToken] = useState(false);
  const [selectedRunnerKey, setSelectedRunnerKey] = useState('debug_status');
  const [runnerPathValue, setRunnerPathValue] = useState('');
  const [runnerRouteParam, setRunnerRouteParam] = useState('');
  const [runnerQueryText, setRunnerQueryText] = useState('{\n  "business_template_id": null\n}');
  const [runnerBodyText, setRunnerBodyText] = useState('{}');
  const [runningCall, setRunningCall] = useState(false);
  const [runnerError, setRunnerError] = useState('');
  const [runnerResult, setRunnerResult] = useState(null);
  const [showTestingTools, setShowTestingTools] = useState(false);

  const canUseTestingTools = !isProductionEnv || Number(userId) === 1;

  const runnerOptions = useMemo(() => {
    const debugOptions = [
      {
        key: 'debug_status',
        label: 'Debug: status',
        description: 'GET /api/mcp/v1/status',
        method: 'GET',
        path: '/api/mcp/v1/status',
        requiredScopes: ['mcp:debug.read'],
        tenantRequired: false,
        queryDefaults: {},
        bodyDefaults: {},
      },
      {
        key: 'debug_tools',
        label: 'Debug: tools',
        description: 'GET /api/mcp/v1/tools',
        method: 'GET',
        path: '/api/mcp/v1/tools',
        requiredScopes: ['mcp:debug.read'],
        tenantRequired: false,
        queryDefaults: {},
        bodyDefaults: {},
      },
      {
        key: 'debug_tokens',
        label: 'Debug: tokens',
        description: 'GET /api/mcp/v1/tokens',
        method: 'GET',
        path: '/api/mcp/v1/tokens',
        requiredScopes: ['mcp:debug.read'],
        tenantRequired: false,
        queryDefaults: { business_template_id: businessTemplateId || null },
        bodyDefaults: {},
      },
      {
        key: 'debug_requests',
        label: 'Debug: requests',
        description: 'GET /api/mcp/v1/requests',
        method: 'GET',
        path: '/api/mcp/v1/requests',
        requiredScopes: ['mcp:debug.read'],
        tenantRequired: false,
        queryDefaults: {},
        bodyDefaults: {},
      },
    ];

    const toolOptions = toolDefinitions.map((tool) => {
      const parsedRoute = parseUpstreamRoute(tool.upstream_route);

      return {
        key: `tool_${tool.name}`,
        label: `Tool: ${tool.name}`,
        description: tool.description,
        method: parsedRoute.method,
        path: parsedRoute.path,
        requiredScopes: tool.required_scopes || [],
        tenantRequired: Boolean(tool.tenant_required),
        queryDefaults: {},
        bodyDefaults: parsedRoute.method === 'POST' ? { business_template_id: businessTemplateId || null } : {},
      };
    });

    return [...debugOptions, ...toolOptions];
  }, [businessTemplateId, toolDefinitions]);

  const activeRunnerOption = useMemo(
    () => runnerOptions.find((option) => option.key === selectedRunnerKey) || runnerOptions[0] || null,
    [runnerOptions, selectedRunnerKey]
  );

  const hasIssuedToken = Boolean(issuedToken?.access_token);
  const activeRunnerRequiredScopes = activeRunnerOption?.requiredScopes || [];
  const issuedTokenScopes = issuedToken?.scopes || [];
  const missingRunnerScopes = activeRunnerRequiredScopes.filter((scope) => !issuedTokenScopes.includes(scope));

  const mcpClient = useMemo(() => {
    if (!hasIssuedToken) {
      return null;
    }

    return axios.create({
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${issuedToken.access_token}`,
      },
    });
  }, [hasIssuedToken, issuedToken]);

  async function loadDashboard(client, tokenMeta = issuedToken) {
    if (!client || !tokenMeta?.access_token) {
      return;
    }

    setLoadingDashboard(true);
    setDashboardError('');

    try {
      const [statusResponse, toolsResponse, tokensResponse, requestsResponse] = await Promise.all([
        client.get('/api/mcp/v1/status'),
        client.get('/api/mcp/v1/tools'),
        client.get('/api/mcp/v1/tokens', {
          params: { business_template_id: businessTemplateId },
        }),
        client.get('/api/mcp/v1/requests'),
      ]);

      setStatus(statusResponse.data?.data || null);
      setTools(toolsResponse.data?.data || []);
      setTokens(tokensResponse.data?.data || []);
      setRequests(requestsResponse.data?.data || []);
    } catch (error) {
      const message = error?.response?.data?.error?.message
        || error?.message
        || 'Failed to load MCP diagnostics.';
      setDashboardError(message);
    } finally {
      setLoadingDashboard(false);
    }
  }

  useEffect(() => {
    if (!mcpClient || !hasIssuedToken) {
      return;
    }

    loadDashboard(mcpClient);
  }, [mcpClient]);

  useEffect(() => {
    if (!selectedRequestId || !mcpClient) {
      setSelectedRequestDetail(null);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const response = await mcpClient.get(`/api/mcp/v1/requests/${selectedRequestId}`);
        if (!cancelled) {
          setSelectedRequestDetail(response.data?.data || null);
        }
      } catch (error) {
        if (!cancelled) {
          setSelectedRequestDetail({
            error: error?.response?.data?.error?.message || 'Failed to load request detail.',
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedRequestId, mcpClient]);

  useEffect(() => {
    if (!selectedTokenId || !mcpClient) {
      setSelectedTokenDetail(null);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const response = await mcpClient.get(`/api/mcp/v1/tokens/${selectedTokenId}`);
        if (!cancelled) {
          setSelectedTokenDetail(response.data?.data || null);
        }
      } catch (error) {
        if (!cancelled) {
          setSelectedTokenDetail({
            error: error?.response?.data?.error?.message || 'Failed to load token detail.',
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedTokenId, mcpClient]);

  useEffect(() => {
    if (!runnerOptions.some((option) => option.key === selectedRunnerKey) && runnerOptions[0]) {
      setSelectedRunnerKey(runnerOptions[0].key);
    }
  }, [runnerOptions, selectedRunnerKey]);

  useEffect(() => {
    if (!activeRunnerOption) {
      return;
    }

    setRunnerPathValue(activeRunnerOption.path || '');
    setRunnerRouteParam('');
    setRunnerQueryText(JSON.stringify(activeRunnerOption.queryDefaults || {}, null, 2));
    setRunnerBodyText(JSON.stringify(activeRunnerOption.bodyDefaults || {}, null, 2));
    setSelectedScopes((current) => Array.from(new Set([...current, ...(activeRunnerOption.requiredScopes || [])])));
    setRunnerError('');
    setRunnerResult(null);
  }, [activeRunnerOption]);

  const toggleScope = (scope) => {
    setSelectedScopes((current) => {
      if (current.includes(scope)) {
        return current.filter((entry) => entry !== scope);
      }

      return [...current, scope];
    });
  };

  const issueToken = async (scopeOverride = null) => {
    setIssuingToken(true);
    setBrokerError('');

    try {
      const scopesToIssue = Array.isArray(scopeOverride) ? scopeOverride : selectedScopes;
      const response = await axios.post('/api/v1/mcp/token', {
        business_template_id: businessTemplateId,
        scopes: scopesToIssue,
        ttl_minutes: Number(ttlMinutes || 60),
        label,
      });

      const tokenMeta = response.data?.data || null;
      setIssuedToken(tokenMeta);
      setSelectedTokenId(tokenMeta?.token_id || '');
      setSelectedTokenDetail(null);
      setSelectedRequestId('');
      setSelectedRequestDetail(null);

      const client = axios.create({
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${tokenMeta.access_token}`,
        },
      });

      await loadDashboard(client, tokenMeta);

      return tokenMeta;
    } catch (error) {
      const message = error?.response?.data?.error?.message
        || error?.message
        || 'Failed to issue MCP token.';
      setBrokerError(message);
      throw error;
    } finally {
      setIssuingToken(false);
    }
  };

  const issueCompatibleToken = async () => {
    const nextScopes = Array.from(new Set([...selectedScopes, ...activeRunnerRequiredScopes]));
    setSelectedScopes(nextScopes);
    await issueToken(nextScopes);
  };

  const revokeSelectedToken = async () => {
    if (!mcpClient || !selectedTokenId) {
      return;
    }

    setRevokingToken(true);
    setDashboardError('');

    try {
      const response = await mcpClient.post(`/api/mcp/v1/tokens/${selectedTokenId}/revoke`);
      const revokedToken = response.data?.data || null;

      if (revokedToken) {
        setSelectedTokenDetail(revokedToken);
      }

      await loadDashboard(mcpClient);
    } catch (error) {
      const message = error?.response?.data?.error?.message
        || error?.message
        || 'Failed to revoke MCP token.';
      setDashboardError(message);
    } finally {
      setRevokingToken(false);
    }
  };

  const loadRunnerOption = (optionKey) => {
    setSelectedRunnerKey(optionKey);
  };

  const runSelectedCall = async () => {
    if (!activeRunnerOption) {
      return;
    }

    const placeholderNames = Array.from(runnerPathValue.matchAll(/\{([^}]+)\}/g)).map((match) => match[1]);
    if (placeholderNames.length > 0 && !runnerRouteParam.trim()) {
      setRunnerError(`A route value is required for: ${placeholderNames.join(', ')}`);
      setRunnerResult(null);
      return;
    }

    setRunningCall(true);
    setRunnerError('');
    setRunnerResult(null);

    try {
      let clientToUse = mcpClient;

      if (!clientToUse || missingRunnerScopes.length > 0) {
        const nextScopes = Array.from(new Set([
          ...selectedScopes,
          ...activeRunnerRequiredScopes,
        ]));
        setSelectedScopes(nextScopes);

        const tokenMeta = await issueToken(nextScopes);
        clientToUse = axios.create({
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${tokenMeta.access_token}`,
          },
        });
      }

      const resolvedPath = placeholderNames.length > 0
        ? runnerPathValue.replace(/\{[^}]+\}/g, encodeURIComponent(runnerRouteParam.trim()))
        : runnerPathValue;

      const query = parseJsonInput(runnerQueryText, {});
      const body = parseJsonInput(runnerBodyText, {});
      const response = await clientToUse.request({
        method: activeRunnerOption.method,
        url: resolvedPath,
        params: query,
        data: ['POST', 'PUT', 'PATCH', 'DELETE'].includes(activeRunnerOption.method) ? body : undefined,
        headers: activeRunnerOption.tenantRequired ? {
          'X-Business-Template-Id': String(businessTemplateId),
        } : undefined,
      });

      setRunnerResult({
        ok: true,
        status: response.status,
        path: resolvedPath,
        method: activeRunnerOption.method,
        data: response.data,
      });

      await loadDashboard(clientToUse);
    } catch (error) {
      const responseStatus = error?.response?.status || null;
      const responseData = error?.response?.data || null;
      const message = error?.response?.data?.error?.message
        || error?.message
        || 'Failed to execute MCP test call.';

      setRunnerError(message);
      setRunnerResult({
        ok: false,
        status: responseStatus,
        path: runnerPathValue,
        method: activeRunnerOption.method,
        data: responseData,
      });
    } finally {
      setRunningCall(false);
    }
  };

  return (
    <div>
      <div className="panel panel-default">
        <div className="panel-heading">
          <h4 className="m-0">MCP Admin Panel</h4>
          <p className="text-muted m-t-5 m-b-0">
            Issue a short-lived MCP token for {businessTemplateTitle || 'the current template'} and inspect the live MCP route surface.
          </p>
        </div>
        <div className="panel-body">
          {brokerError ? (
            <div className="alert alert-danger">{brokerError}</div>
          ) : null}

          <div className="row">
            <div className="col-md-4">
              <div className="form-group">
                <label className="control-label">Business Template</label>
                <input className="form-control" value={`${businessTemplateTitle || 'Current Template'} (#${businessTemplateId})`} readOnly />
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-group">
                <label className="control-label">Token Label</label>
                <input className="form-control" value={label} onChange={(event) => setLabel(event.target.value)} />
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-group">
                <label className="control-label">TTL Minutes</label>
                <input className="form-control" type="number" min="1" value={ttlMinutes} onChange={(event) => setTtlMinutes(event.target.value)} />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="control-label">Scopes</label>
            <div className="row">
              {allowedScopes.map((scope) => (
                <div className="col-sm-6 col-md-4" key={scope}>
                  <label className="checkbox-inline" style={{ display: 'block', marginLeft: 0, paddingLeft: 0 }}>
                    <input
                      type="checkbox"
                      checked={selectedScopes.includes(scope)}
                      onChange={() => toggleScope(scope)}
                      style={{ marginRight: 8 }}
                    />
                    {scope}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="clearfix">
            <button
              type="button"
              className="btn btn-primary"
              disabled={issuingToken || selectedScopes.length === 0}
              onClick={() => issueToken()}
            >
              {issuingToken ? 'Issuing MCP Token...' : 'Issue MCP Token'}
            </button>
            <button
              type="button"
              className="btn btn-default m-l-10"
              disabled={!hasIssuedToken || loadingDashboard}
              onClick={() => loadDashboard(mcpClient)}
            >
              Refresh Diagnostics
            </button>
          </div>

          {issuedToken ? (
            <div className="alert alert-info m-t-15 m-b-0">
              <div><strong>Token ID:</strong> {issuedToken.token_id}</div>
              <div><strong>Expires:</strong> {formatDateTime(issuedToken.expires_at)}</div>
              <div><strong>Scopes:</strong> {(issuedToken.scopes || []).join(', ') || 'N/A'}</div>
              <div className="m-t-10 text-muted">
                The access token is kept in browser memory for this session and is not rendered in the admin UI.
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {dashboardError ? (
        <div className="alert alert-danger">{dashboardError}</div>
      ) : null}

      <div className="row">
        <div className="col-md-3">
          <div className="panel panel-default">
            <div className="panel-heading"><h5 className="m-0">Status</h5></div>
            <div className="panel-body">
              {loadingDashboard ? 'Loading...' : (
                <div>
                  <div><strong>Healthy:</strong> {status?.healthy ? 'Yes' : 'No'}</div>
                  <div><strong>Registered Tools:</strong> {status?.registered_tools ?? 0}</div>
                  <div><strong>Recent Requests:</strong> {status?.recent_request_count ?? 0}</div>
                  <div><strong>Recent Failures:</strong> {status?.recent_failure_count ?? 0}</div>
                  <div><strong>Last Request:</strong> {formatDateTime(status?.last_request_at)}</div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-9">
          <div className="panel panel-default">
            <div className="panel-heading"><h5 className="m-0">Registered Tools</h5></div>
            <div className="panel-body table-responsive">
              <table className="table table-condensed table-striped">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Mode</th>
                    <th>Scopes</th>
                    <th>Upstream Route</th>
                    {canUseTestingTools ? <th>Test</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {tools.length === 0 ? (
                    <tr><td colSpan={canUseTestingTools ? '5' : '4'} className="text-muted">No MCP tools loaded.</td></tr>
                  ) : tools.map((tool) => (
                    <tr key={tool.name}>
                      <td>
                        <div className="f-w-700">{tool.name}</div>
                        <div className="text-muted">{tool.description}</div>
                      </td>
                      <td>{tool.mutation_mode}</td>
                      <td>{(tool.required_scopes || []).join(', ') || 'N/A'}</td>
                      <td>{tool.upstream_route}</td>
                      {canUseTestingTools ? (
                        <td>
                          <button
                            type="button"
                            className="btn btn-default btn-xs"
                            onClick={() => {
                              loadRunnerOption(`tool_${tool.name}`);
                              setShowTestingTools(true);
                            }}
                          >
                            Load In Runner
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {canUseTestingTools ? (
        <div className="panel panel-default">
          <div className="panel-heading clearfix">
            <h5 className="m-0 pull-left">MCP Testing Tools</h5>
            <button
              type="button"
              className="btn btn-default btn-sm pull-right"
              onClick={() => setShowTestingTools((current) => !current)}
            >
              {showTestingTools ? 'Hide Testing Tools' : 'Show Testing Tools'}
            </button>
          </div>
          {showTestingTools ? (
            <div className="panel-body">
              <p className="text-muted">
                Run real MCP routes from this admin page using the brokered token currently held in browser memory.
              </p>

              {runnerError ? (
                <div className="alert alert-danger">{runnerError}</div>
              ) : null}

              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label className="control-label">Preset</label>
                    <select
                      className="form-control"
                      value={selectedRunnerKey}
                      onChange={(event) => setSelectedRunnerKey(event.target.value)}
                    >
                      {runnerOptions.map((option) => (
                        <option key={option.key} value={option.key}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="form-group">
                    <label className="control-label">Method</label>
                    <input className="form-control" value={activeRunnerOption?.method || 'GET'} readOnly />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="control-label">Route Path</label>
                    <input className="form-control" value={runnerPathValue} onChange={(event) => setRunnerPathValue(event.target.value)} disabled={!hasIssuedToken} />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label className="control-label">Route Parameter</label>
                    <input
                      className="form-control"
                      value={runnerRouteParam}
                      onChange={(event) => setRunnerRouteParam(event.target.value)}
                      placeholder="Used for {id} or similar placeholders"
                    />
                  </div>
                </div>
                <div className="col-md-8">
                  <div className="alert alert-info m-b-0">
                    <strong>Tenant Header:</strong> {activeRunnerOption?.tenantRequired ? `X-Business-Template-Id: ${businessTemplateId}` : 'Not required for this preset'}
                  </div>
                </div>
              </div>

              <div className="row m-t-10">
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="control-label">Query JSON</label>
                    <textarea
                      className="form-control"
                      rows="8"
                      value={runnerQueryText}
                      onChange={(event) => setRunnerQueryText(event.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="control-label">Body JSON</label>
                    <textarea
                      className="form-control"
                      rows="8"
                      value={runnerBodyText}
                      onChange={(event) => setRunnerBodyText(event.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="row m-t-10">
                <div className="col-md-6">
                  <div className="alert alert-info m-b-0">
                    <div><strong>Required Scopes:</strong> {activeRunnerRequiredScopes.join(', ') || 'None'}</div>
                    <div><strong>Current Token Scopes:</strong> {issuedTokenScopes.join(', ') || 'No token issued'}</div>
                  </div>
                </div>
                <div className="col-md-6">
                  {missingRunnerScopes.length > 0 ? (
                    <div className="alert alert-warning m-b-0">
                      <div><strong>This preset needs:</strong> {missingRunnerScopes.join(', ')}</div>
                      <div className="m-t-5 text-muted">
                        `Run MCP Call` will issue or refresh a compatible token automatically.
                      </div>
                    </div>
                  ) : (
                    <div className="alert alert-success m-b-0">
                      {hasIssuedToken
                        ? 'The current token covers the scopes required by this runner preset.'
                        : 'Run MCP Call will issue a compatible token automatically if one is needed.'}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={runningCall || issuingToken}
                  onClick={runSelectedCall}
                >
                  {runningCall ? 'Running MCP Call...' : 'Run MCP Call'}
                </button>
              </div>

              {!hasIssuedToken ? (
                <div className="alert alert-warning m-t-15 m-b-0">
                  No MCP token is loaded yet. Running the call will issue a compatible token automatically.
                </div>
              ) : null}

              {runnerResult ? (
                <div className={`alert ${runnerResult.ok ? 'alert-success' : 'alert-danger'} m-t-15 m-b-0`}>
                  <div><strong>Method:</strong> {runnerResult.method}</div>
                  <div><strong>Path:</strong> {runnerResult.path}</div>
                  <div><strong>Status:</strong> {runnerResult.status ?? 'N/A'}</div>
                  <div className="m-t-10"><strong>Response</strong></div>
                  <pre className="well well-sm" style={{ maxHeight: 300, overflowY: 'auto' }}>{JSON.stringify(runnerResult.data || {}, null, 2)}</pre>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="row">
        <div className="col-md-6">
          <div className="panel panel-default">
            <div className="panel-heading"><h5 className="m-0">Brokered MCP Tokens</h5></div>
            <div className="panel-body table-responsive">
              <table className="table table-condensed table-striped">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Expires</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tokens.length === 0 ? (
                    <tr><td colSpan="3" className="text-muted">No MCP tokens found.</td></tr>
                  ) : tokens.map((token) => (
                    <tr
                      key={token.id}
                      onClick={() => setSelectedTokenId(String(token.id))}
                      style={{ cursor: 'pointer', backgroundColor: String(selectedTokenId) === String(token.id) ? '#f5f5f5' : undefined }}
                    >
                      <td>
                        <div className="f-w-700">{token.id}</div>
                        <div className="text-muted">{token.name}</div>
                      </td>
                      <td>{formatDateTime(token.expires_at)}</td>
                      <td>
                        <span className={token.revoked || token.is_expired ? 'label label-danger' : 'label label-success'}>
                          {token.revoked ? 'Revoked' : token.is_expired ? 'Expired' : 'Active'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="panel panel-default">
            <div className="panel-heading"><h5 className="m-0">Token Detail</h5></div>
            <div className="panel-body">
              {!selectedTokenDetail ? (
                <div className="text-muted">Select a token to inspect its scope set and expiry metadata.</div>
              ) : selectedTokenDetail.error ? (
                <div className="alert alert-danger m-b-0">{selectedTokenDetail.error}</div>
              ) : (
                <div>
                  <div><strong>Name:</strong> {selectedTokenDetail.name}</div>
                  <div><strong>Business Template:</strong> {selectedTokenDetail.business_template_id || 'N/A'}</div>
                  <div><strong>Created:</strong> {formatDateTime(selectedTokenDetail.created_at)}</div>
                  <div><strong>Expires:</strong> {formatDateTime(selectedTokenDetail.expires_at)}</div>
                  <div><strong>Minutes Remaining:</strong> {selectedTokenDetail.expires_in_minutes ?? 'Expired'}</div>
                  <div className="m-t-10">
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      disabled={revokingToken || Boolean(selectedTokenDetail.revoked)}
                      onClick={revokeSelectedToken}
                    >
                      {revokingToken ? 'Revoking...' : selectedTokenDetail.revoked ? 'Token Revoked' : 'Revoke Token'}
                    </button>
                  </div>
                  <div className="m-t-10"><strong>Scopes</strong></div>
                  <ul className="m-b-0">
                    {(selectedTokenDetail.scopes || []).map((scope) => <li key={scope}>{scope}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6">
          <div className="panel panel-default">
            <div className="panel-heading"><h5 className="m-0">Recent MCP Requests</h5></div>
            <div className="panel-body table-responsive">
              <table className="table table-condensed table-striped">
                <thead>
                  <tr>
                    <th>Request</th>
                    <th>Status</th>
                    <th>Latency</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.length === 0 ? (
                    <tr><td colSpan="3" className="text-muted">No MCP requests recorded yet.</td></tr>
                  ) : requests.map((request) => (
                    <tr
                      key={request.request_id}
                      onClick={() => setSelectedRequestId(request.request_id)}
                      style={{ cursor: 'pointer', backgroundColor: selectedRequestId === request.request_id ? '#f5f5f5' : undefined }}
                    >
                      <td>
                        <div className="f-w-700">{request.request_id}</div>
                        <div className="text-muted">{request.method} {request.path}</div>
                      </td>
                      <td><span className={statusLabelClass(request.status)}>{request.status ?? 'N/A'}</span></td>
                      <td>{request.duration_ms ?? 0} ms</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="panel panel-default">
            <div className="panel-heading"><h5 className="m-0">Request Detail</h5></div>
            <div className="panel-body">
              {!selectedRequestDetail ? (
                <div className="text-muted">Select a request to inspect its sanitized payload and route metadata.</div>
              ) : selectedRequestDetail.error ? (
                <div className="alert alert-danger m-b-0">{selectedRequestDetail.error}</div>
              ) : (
                <div>
                  <div><strong>Route:</strong> {selectedRequestDetail.route_name || 'N/A'}</div>
                  <div><strong>Time:</strong> {formatDateTime(selectedRequestDetail.timestamp)}</div>
                  <div><strong>Business Template:</strong> {selectedRequestDetail.business_template_id || 'N/A'}</div>
                  <div><strong>Query</strong></div>
                  <pre className="well well-sm" style={{ maxHeight: 140, overflowY: 'auto' }}>{JSON.stringify(selectedRequestDetail.query || {}, null, 2)}</pre>
                  <div><strong>Body</strong></div>
                  <pre className="well well-sm" style={{ maxHeight: 180, overflowY: 'auto' }}>{JSON.stringify(selectedRequestDetail.body || {}, null, 2)}</pre>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
